import { FormEvent, useState } from 'react'
import { LeadFormValues, NEED_OPTIONS, NeedId } from '../../types/lead'
import { FORMSPREE_ENDPOINT } from '../../data/site'
import { trackLead } from '../../utils/metaPixel'

const emptyValues: LeadFormValues = {
  nombre: '',
  email: '',
  telefono: '',
  florida: '',
  etapa: '',
  necesidades: [],
  mensaje: '',
}

export type LeadFormErrors = Partial<Record<keyof LeadFormValues, string>>
export type LeadFormStatus = 'idle' | 'sending' | 'error' | 'success'

function needLabels(ids: NeedId[]): string {
  return ids
    .map((id) => NEED_OPTIONS.find((option) => option.id === id)?.label ?? id)
    .join(', ')
}

export function useLeadForm() {
  const [values, setValues] = useState<LeadFormValues>(emptyValues)
  const [errors, setErrors] = useState<LeadFormErrors>({})
  const [step, setStep] = useState<1 | 2>(1)
  const [status, setStatus] = useState<LeadFormStatus>('idle')
  const [started, setStarted] = useState(false)

  const setField = <K extends keyof LeadFormValues>(key: K, value: LeadFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  const toggleNeed = (id: NeedId) => {
    setValues((prev) => ({
      ...prev,
      necesidades: prev.necesidades.includes(id)
        ? prev.necesidades.filter((n) => n !== id)
        : [...prev.necesidades, id],
    }))
  }

  const validateStepOne = (): LeadFormErrors => {
    const next: LeadFormErrors = {}
    if (!values.florida) next.florida = 'Selecciona una opción.'
    if (!values.etapa) next.etapa = 'Selecciona una opción.'
    return next
  }

  const validateStepTwo = (): LeadFormErrors => {
    const next: LeadFormErrors = {}
    if (!values.nombre.trim()) next.nombre = 'Por favor escribe tu nombre.'
    if (!values.email.trim()) next.email = 'Por favor escribe tu email.'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim()))
      next.email = 'Revisa el formato de tu email.'
    if (!values.telefono.trim()) next.telefono = 'Por favor escribe tu teléfono.'
    return next
  }

  const goNext = () => {
    const next = validateStepOne()
    setErrors(next)
    if (Object.keys(next).length > 0) return
    setStep(2)
  }

  const goBack = () => {
    setErrors({})
    setStep(1)
  }

  const send = async () => {
    setStatus('sending')
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          Nombre: values.nombre.trim(),
          Email: values.email.trim(),
          Teléfono: values.telefono.trim(),
          '¿Tu negocio estará registrado en Florida?': values.florida,
          '¿En qué etapa estás?': values.etapa,
          '¿Qué necesitas?': needLabels(values.necesidades),
          'Cuéntanos brevemente sobre tu negocio': values.mensaje.trim(),
        }),
      })

      if (!response.ok) {
        setStatus('error')
        return
      }
      setStatus('success')
      // Lead solo se dispara con la confirmación de Formspree.
      trackLead()
    } catch {
      setStatus('error')
    }
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (step === 1) {
      goNext()
      return
    }
    const next = validateStepTwo()
    setErrors(next)
    if (Object.keys(next).length > 0) return
    void send()
  }

  const reset = () => {
    setValues(emptyValues)
    setErrors({})
    setStep(1)
    setStatus('idle')
  }

  return {
    values,
    errors,
    step,
    status,
    submitted: status === 'success',
    started,
    markStarted: () => setStarted(true),
    setField,
    toggleNeed,
    goNext,
    goBack,
    handleSubmit,
    reset,
  }
}
